import React, { useRef, useState } from 'react';
import { ButtonColor } from '../../../components/uikit/buttons/Button.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { CustomModal } from '../../../components/uikit/modal/CustomModal.tsx';
import { AdvancedFormReturnType } from '../../../helpers/form/useAdvancedForm.ts';
import clsx from 'clsx';
import { UserRole } from '../../../services/api/api-client.types.ts';
import { useWatch } from 'react-hook-form';
import Cross from 'assets/icons/close-button-small.svg?react';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import styles from './AccessSettingsModal.module.scss';
import { Form } from './DesignerNewPage.tsx';
import { Checkbox } from '../../../components/uikit/inputs/Checkbox.tsx';
import { Tip } from '../../../components/uikit/Tip.tsx';
import SearchIcon from 'assets/icons/search-blue.svg?react';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import { IconButton } from '../../../components/uikit/IconButton.tsx';

type SelectedCountry = {
  label: string;
  value: string;
};

type OwnProps = {
  closeModal: () => void;
  changeAccessSettings: () => Promise<void>;
  form: AdvancedFormReturnType<Form>;
  countryOptions: { value: string; label: string }[];
};

// to hide Admin, because he has access to all pages
const allAvailableRoles = Object.values(UserRole).filter(
  (role) => role !== UserRole.Admin,
) as UserRole[];

export const AccessSettingsModal = (props: OwnProps) => {
  const i18n = useScopedTranslation('Page.PageDesigner');

  const [countryInputValue, setCountryInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [isRolesTabActive, setIsRolesTabActive] = useState(true);

  const { closeModal, changeAccessSettings, form, countryOptions } = props;

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      await changeAccessSettings();
      closeModal();
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const [selectedRoles, selectedCountries] = useWatch({
    control: form.control,
    name: ['selectedRoles', 'selectedCountries'],
  });

  const initialValuesRef = useRef({
    selectedRoles: form.getValues('selectedRoles'),
    selectedCountries: form.getValues('selectedCountries'),
  });

  const setRoles = (roles: UserRole[], shouldDirty = true) => {
    form.setValue('selectedRoles', roles, { shouldDirty });
    if (shouldDirty) {
      setIsSubmitButtonDisabled(false);
    }
  };

  const setCountries = (countries: SelectedCountry[], shouldDirty = true) => {
    form.setValue('selectedCountries', countries, { shouldDirty });
    if (shouldDirty) {
      setIsSubmitButtonDisabled(false);
    }
  };

  const removeSelectedRole = (role: UserRole) => {
    setRoles(selectedRoles.filter((r) => r !== role));
  };

  const removeSelectedCountry = (country: SelectedCountry) => {
    setCountries(selectedCountries.filter((c) => c.value !== country.value));
  };

  const handleCancel = () => {
    setCountries(initialValuesRef.current.selectedCountries, false);
    setRoles(initialValuesRef.current.selectedRoles, false);
    setIsSubmitButtonDisabled(true);
    closeModal();
  };

  const filteredCountryOptions = countryOptions.filter((countryOption) =>
    countryOption.label.toLowerCase().includes(countryInputValue.toLowerCase()),
  );

  const total = filteredCountryOptions.length;
  const base = Math.floor(total / 3);
  const remainder = total % 3;

  const sizes = [
    base + (remainder > 0 ? 1 : 0),
    base + (remainder > 1 ? 1 : 0),
    base,
  ];

  const columns = [
    filteredCountryOptions.slice(0, sizes[0]),
    filteredCountryOptions.slice(sizes[0], sizes[0] + sizes[1]),
    filteredCountryOptions.slice(sizes[0] + sizes[1]),
  ];

  const isAllRolesChecked = selectedRoles.length === allAvailableRoles.length;
  const isAllCountriesChecked =
    selectedCountries.length === countryOptions.length;

  return (
    <CustomModal
      isOpen={true}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      cancelButtonText={i18n.t('cancel')}
      submitButtonText={i18n.t('save')}
      submitButtonType={ButtonColor.Primary}
      cancelButtonType={ButtonColor.Secondary}
      titleText={i18n.t('access_settings')}
      containerClassName={styles.modalBodyWidth700}
      contentClassName={styles.scrollBar}
      buttonContainerClassName={styles.buttonContainer}
      isDisabled={isSubmitButtonDisabled}
      textClassName={styles.text}
      doNotCloseOnOutsideClick={true}
    >
      <Loading
        loading={isLoading}
        wrapperClassName={styles.loader}
        className={styles.absoluteContainer}
      />
      <div className={clsx(styles.rowContainer, styles.marginLeftMinus4)}>
        <div
          className={clsx(styles.tab, isRolesTabActive && styles.activeTab)}
          onClick={() => setIsRolesTabActive(true)}
        >
          {i18n.t('roles')}
        </div>
        <div
          className={clsx(styles.tab, !isRolesTabActive && styles.activeTab)}
          onClick={() => setIsRolesTabActive(false)}
        >
          {i18n.t('countries')}
        </div>
        <div className={styles.headerDivider} />
      </div>
      {isRolesTabActive ? (
        <div className={styles.bodyContainer}>
          <Checkbox
            containerClassName={styles.selectAllCheckbox}
            actionText={i18n.t('select_all') + ` (${allAvailableRoles.length})`}
            halfChecked={selectedRoles.length > 0 && !isAllRolesChecked}
            isChecked={isAllRolesChecked}
            onChange={(e) => {
              if (isAllRolesChecked) {
                setRoles([]);
              } else {
                setRoles(allAvailableRoles);
              }
            }}
          />
          <div className={styles.bodyDivider} />
          <div className={styles.checkboxContainer}>
            {allAvailableRoles.map((role) => (
              <Checkbox
                key={role}
                actionText={role}
                onChange={(e) => {
                  if (e.target.checked) {
                    const newRoles = [...selectedRoles, role];
                    setRoles(newRoles);
                  } else {
                    removeSelectedRole(role);
                  }
                }}
                isChecked={selectedRoles.includes(role)}
              />
            ))}
          </div>
          <div className={styles.headerDivider} />
        </div>
      ) : (
        <div className={styles.bodyContainer}>
          <div className={clsx(styles.marginTop24, styles.relative)}>
            <SearchIcon className={styles.searchIcon} />
            <Input
              value={countryInputValue}
              onChange={(e) => setCountryInputValue(e.target.value)}
              placeholder={i18n.t('search_countries')}
              className={styles.searchBarContainer}
              endAdornment={
                <div>
                  {countryInputValue && (
                    <IconButton
                      iconClassName={styles.crossButton}
                      type={'destroy'}
                      onClick={() => setCountryInputValue('')}
                    >
                      <Cross />
                    </IconButton>
                  )}
                </div>
              }
              endAdornmentClassname={styles.cross}
            />
          </div>
          <Checkbox
            containerClassName={styles.selectAllCheckbox}
            actionText={i18n.t('select_all') + ` (${countryOptions.length})`}
            halfChecked={selectedCountries.length > 0 && !isAllCountriesChecked}
            isChecked={isAllCountriesChecked}
            onChange={(e) => {
              if (isAllCountriesChecked) {
                setCountries([]);
              } else {
                setCountries(countryOptions);
              }
            }}
          />
          <div className={styles.bodyDivider} />
          <div className={clsx(styles.rowContainer, styles.overflowContainer)}>
            {columns.map((column, index) => (
              <div key={index} className={styles.countryContainer}>
                {column.map((countryOption) => (
                  <Tip
                    key={countryOption.value}
                    tooltipText={countryOption.label}
                    textBody={
                      <Checkbox
                        actionTextClassName={styles.checkboxActionText}
                        containerClassName={styles.a}
                        actionText={countryOption.label}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const newCountries = [
                              ...selectedCountries,
                              countryOption,
                            ];
                            setCountries(newCountries);
                          } else {
                            removeSelectedCountry(countryOption);
                          }
                        }}
                        isChecked={
                          !!selectedCountries.some(
                            (selectedCountry) =>
                              selectedCountry.value === countryOption.value,
                          )
                        }
                      />
                    }
                    className={styles.tooltip}
                    tooltipClassName={styles.tipMargins}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className={styles.headerDivider} />
        </div>
      )}
    </CustomModal>
  );
};
